import { AlertTriangle, CheckCircle, Target } from 'lucide-react'
import { useState } from 'react'
import { InitiativeCard } from './initiative-card'
import { InitiativePreviewPane } from './initiative-preview-pane'
import { motion } from 'framer-motion'

interface Recommendation {
  id: string
  assessment_id?: string
  type?: string
  initiative_id?: string
  project_initiative_id?: string
  title: string
  description: string
  short_description?: string
  priority: 'high' | 'medium' | 'low' | 'critical'
  category: string
  domain?: string
  impact_level?: string
  effort_level?: string
  from_level?: number
  to_level?: number
  from_maturity_level?: number
  to_maturity_level?: number
  duration_weeks?: number
  team_size_min?: number
  team_size_max?: number
  budget_range_min?: number
  budget_range_max?: number
  project_initiative?: Record<string, any>
}

interface RecommendationsProps {
  recommendations: Recommendation[]
}

// ── Animation variants ───────────────────────────────────────────────────
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
}

const sectionVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const },
  },
}

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.06,
      duration: 0.35,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  }),
}

export function Recommendations({ recommendations }: RecommendationsProps) {
  const [selectedInitiative, setSelectedInitiative] = useState<Recommendation | null>(null)

  const criticalPriorityRecs = recommendations.filter(r => r.priority === 'critical')
  const highPriorityRecs = recommendations.filter(r => r.priority === 'high')
  const mediumPriorityRecs = recommendations.filter(r => r.priority === 'medium')
  const lowPriorityRecs = recommendations.filter(r => r.priority === 'low')

  const renderPriorityGroup = (label: string, Icon: React.ElementType, iconColor: string, recs: Recommendation[], groupIndex: number) => {
    if (recs.length === 0) return null
    return (
      <motion.div
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: groupIndex * 0.15 }}
      >
        <motion.h3
          className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2"
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: groupIndex * 0.15, duration: 0.3 }}
        >
          <Icon className={`h-5 w-5 ${iconColor}`} />
          <span>{label} ({recs.length})</span>
        </motion.h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {recs.map((rec, i) => (
            <motion.div
              key={rec.id}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
            >
              <InitiativeCard
                initiative={rec}
                onViewDetails={() => setSelectedInitiative(rec)}
              />
            </motion.div>
          ))}
        </div>
      </motion.div>
    )
  }

  return (
    <>
      <motion.div
        className="space-y-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {renderPriorityGroup('Critical Priority', AlertTriangle, 'text-red-700', criticalPriorityRecs, 0)}
        {renderPriorityGroup('High Priority', AlertTriangle, 'text-red-600', highPriorityRecs, 1)}
        {renderPriorityGroup('Medium Priority', Target, 'text-yellow-600', mediumPriorityRecs, 2)}
        {renderPriorityGroup('Low Priority', CheckCircle, 'text-green-600', lowPriorityRecs, 3)}

        {recommendations.length === 0 && (
          <motion.div
            className="text-center py-8"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Excellent Performance!
            </h3>
            <p className="text-gray-600">
              Your IT maturity assessment shows strong performance across all domains.
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Initiative Preview Pane */}
      <InitiativePreviewPane
        initiative={selectedInitiative}
        onClose={() => setSelectedInitiative(null)}
      />
    </>
  )
}
